import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import type { Skill } from '@freelancer/datastore/collections';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { HeadingType } from '@freelancer/ui/heading';
import { TagType } from '@freelancer/ui/tag';
import { FontColor, TextSize } from '@freelancer/ui/text';

@Component({
  selector: 'fl-skills-selected',
  template: `
    <fl-heading
      class="SelectedSkillsHeading"
      [headingType]="HeadingType.H3"
      [size]="TextSize.XSMALL"
    >
      <ng-container *ngIf="maxSkillsLimit">
        <ng-container
          *ngIf="selectedSkills?.length > 0; else noSelectedSkills"
          i18n="Number of selected skills heading"
        >
          {{ selectedSkills?.length }} out of {{ maxSkillsLimit }} skills
          selected
        </ng-container>
      </ng-container>

      <ng-template #noSelectedSkills>
        <ng-container i18n="No selected skills heading">
          0 skills selected
        </ng-container>
      </ng-template>
    </fl-heading>

    <fl-scrollable-content
      *ngIf="selectedSkills?.length > 0; else noSelectedSkillsMessage"
      class="SelectedSkillsContent"
    >
      <p
        *ngIf="matchingSkillCount"
        i18n="Number of jobs matching the skills"
        [flMarginBottom]="'small'"
      >
        {{ matchingSkillCount }} jobs matching your skills
      </p>

      <fl-tag
        *ngFor="let skill of selectedSkills; trackBy: trackBySkillId"
        [flMarginBottom]="'xxsmall'"
        [flMarginRight]="'xxsmall'"
        [type]="TagType.DISMISSABLE"
        (dismiss)="handleDeselectedSkill(skill)"
      >
        {{ skill.name }}
      </fl-tag>
    </fl-scrollable-content>

    <ng-template #noSelectedSkillsMessage>
      <ng-container *ngIf="showErrorMessage; else default">
        <fl-banner-alert
          class="Alert"
          bannerTitle="Please select at least one skill."
          i18n-bannerTitle="No selected skills error message"
          [marginBottom]="'none'"
          [type]="BannerAlertType.ERROR"
          [closeable]="false"
        />
      </ng-container>
      <ng-template #default>
        <p
          class="SelectedSkillsContent"
          i18n="No selected skills message"
        >
          Select at least one skill to help us recommend customized jobs for
          you.
        </p>
      </ng-template>
    </ng-template>
  `,
  styleUrls: ['./skills-selected.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillsSelectedComponent {
  BannerAlertType = BannerAlertType;
  HeadingType = HeadingType;
  FontColor = FontColor;
  TagType = TagType;
  TextSize = TextSize;

  @Input() showErrorMessage = false;
  @Input() matchingSkillCount?: number;
  @Input() maxSkillsLimit?: number;
  @Input() selectedSkills?: readonly Skill[] = [];
  @Output() deselectSkill = new EventEmitter<Skill>();

  handleDeselectedSkill(skill: Skill): void {
    this.deselectSkill.emit(skill);
  }

  trackBySkillId(index: number, skill: Skill): number {
    return skill.id;
  }
}
